import type { ReceiptDraft, Tracking } from "../../types/receipt";
import LoadingLabel from "../../components/LoadingLabel";
import ReceiptTrackingItem from "./ReceiptTrackingItem";
import { useReceiptTracking } from "./use-receipt-tracking";

// The step after a receipt is saved: recording what each item cost, so prices can be compared
// across payees later. Separate from the scan because it is optional and can be returned to.

export function ReceiptTracking({
  draft,
  initial = [],
  initialSelectedIds = [],
  onDone,
}: {
  draft: ReceiptDraft;
  initial?: Tracking[];
  initialSelectedIds?: string[];
  onDone?: () => void;
}) {
  const { products, items, setItems, selected, setSelected, statuses, busy, error, message, remaining, save } =
    useReceiptTracking({ draft, initial, initialSelectedIds, onDone });
  return (
    <section className="receipt-tracking receipt-ui">
      <h3>
        Track item prices <span className="muted">Optional</span>
      </h3>
      <p className="muted">
        Pick only the few items worth comparing. Nothing is tracked until you
        choose it.
      </p>
      {draft.adjustments && (
        <p className="receipt-warning">
          {draft.adjustments} Review line prices for discounts and tax before
          comparing.
        </p>
      )}
      <datalist id="receipt-groups">
        {[...new Set(products.map((p) => p.group))].map((g) => (
          <option key={g} value={g} />
        ))}
      </datalist>
      <div className="receipt-actions">
        <button
          type="button"
          disabled={busy}
          onClick={() =>
            setSelected(
              items.filter((i) => statuses[i.id] !== "saved").map((i) => i.id),
            )
          }
        >
          Select all
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() =>
            setSelected(
              items
                .filter((i) => statuses[i.id] === "pending")
                .map((i) => i.id),
            )
          }
        >
          Clear new picks
        </button>
      </div>
      {!items.length && (
        <>
          <p className="muted">
            No items were selected for price tracking on this receipt.
          </p>
          <button className="primary-button" type="button" onClick={onDone}>
            Done
          </button>
        </>
      )}
      {items.map((item) => (
        <ReceiptTrackingItem
          key={item.id}
          item={item}
          currency={draft.currency}
          products={products}
          status={statuses[item.id]}
          isSelected={selected.includes(item.id)}
          busy={busy}
          onToggle={() =>
            setSelected((s) =>
              s.includes(item.id) ? s.filter((id) => id !== item.id) : [...s, item.id],
            )
          }
          onChange={(patch) =>
            setItems((v) => v.map((i) => (i.id === item.id ? { ...i, ...patch } : i)))
          }
        />
      ))}
      {error && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}
      {message && <p role="status">{message}</p>}
      {remaining > 0 && (
        <button
          className="primary-button"
          disabled={busy}
          onClick={() => void save()}
        >
          {busy
            ? <LoadingLabel>Saving prices</LoadingLabel>
            : Object.values(statuses).includes("pending")
              ? "Check & save prices"
              : `Save ${remaining} tracked item${remaining === 1 ? "" : "s"}`}
        </button>
      )}
    </section>
  );
}
